import { useState } from 'react'
import { leadsApi } from '../api'
import { Lead, LeadStage, Priority, STAGE_LABELS } from '../types'

interface LeadFormProps {
  lead?: Lead
  onClose: () => void
  onSaved: (lead: Lead) => void
}

export default function LeadForm({ lead, onClose, onSaved }: LeadFormProps) {
  const [formData, setFormData] = useState({
    coaching_name: lead?.coaching_name || '',
    owner_name: lead?.owner_name || '',
    phone: lead?.phone || '',
    whatsapp: lead?.whatsapp || '',
    email: lead?.email || '',
    district: lead?.district || '',
    student_count: lead?.student_count || 0,
    stage: lead?.stage || 'new_lead' as LeadStage,
    priority: lead?.priority || 'medium' as Priority,
    next_followup: lead?.next_followup || '',
    notes: lead?.notes || '',
    lead_source: lead?.lead_source || 'direct',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      let savedLead: Lead
      if (lead) {
        savedLead = await leadsApi.update(lead.id, formData)
      } else {
        savedLead = await leadsApi.create(formData)
      }
      onSaved(savedLead)
    } catch (err) {
      setError('Failed to save lead. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end lg:items-center justify-center z-50">
      <div className="bg-white rounded-t-xl lg:rounded-xl shadow-xl w-full lg:max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 lg:p-6 border-b border-gray-200 sticky top-0 bg-white">
          <div className="flex justify-between items-center">
            <h2 className="text-lg lg:text-xl font-semibold">
              {lead ? 'Edit Lead' : 'Add New Lead'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              ✕
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 lg:p-6 space-y-3 lg:space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
            <div>
              <label className="label text-xs lg:text-sm">Coaching Center Name *</label>
              <input
                type="text"
                required
                value={formData.coaching_name}
                onChange={(e) => setFormData({ ...formData, coaching_name: e.target.value })}
                className="input-field text-sm lg:text-base"
                placeholder="Enter coaching name"
              />
            </div>
            <div>
              <label className="label text-xs lg:text-sm">Owner Name *</label>
              <input
                type="text"
                required
                value={formData.owner_name}
                onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                className="input-field text-sm lg:text-base"
                placeholder="Enter owner name"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
            <div>
              <label className="label text-xs lg:text-sm">Phone *</label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="input-field text-sm lg:text-base"
                placeholder="Enter phone number"
              />
            </div>
            <div>
              <label className="label text-xs lg:text-sm">WhatsApp</label>
              <input
                type="tel"
                value={formData.whatsapp}
                onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                className="input-field text-sm lg:text-base"
                placeholder="Enter WhatsApp number"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
            <div>
              <label className="label text-xs lg:text-sm">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-field text-sm lg:text-base"
                placeholder="Enter email address"
              />
            </div>
            <div>
              <label className="label text-xs lg:text-sm">District</label>
              <input
                type="text"
                value={formData.district}
                onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                className="input-field text-sm lg:text-base"
                placeholder="Enter district"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
            <div>
              <label className="label text-xs lg:text-sm">Student Count</label>
              <input
                type="number"
                min="0"
                value={formData.student_count}
                onChange={(e) => setFormData({ ...formData, student_count: parseInt(e.target.value) || 0 })}
                className="input-field text-sm lg:text-base"
              />
            </div>
            <div>
              <label className="label text-xs lg:text-sm">Lead Source</label>
              <select
                value={formData.lead_source}
                onChange={(e) => setFormData({ ...formData, lead_source: e.target.value })}
                className="input-field text-sm lg:text-base"
              >
                <option value="direct">Direct</option>
                <option value="referral">Referral</option>
                <option value="website">Website</option>
                <option value="walk-in">Walk-in</option>
                <option value="social-media">Social Media</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
            <div>
              <label className="label text-xs lg:text-sm">Stage</label>
              <select
                value={formData.stage}
                onChange={(e) => setFormData({ ...formData, stage: e.target.value as LeadStage })}
                className="input-field text-sm lg:text-base"
              >
                {Object.entries(STAGE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label text-xs lg:text-sm">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as Priority })}
                className="input-field text-sm lg:text-base"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div>
            <label className="label text-xs lg:text-sm">Next Follow-up</label>
            <input
              type="datetime-local"
              value={formData.next_followup}
              onChange={(e) => setFormData({ ...formData, next_followup: e.target.value })}
              className="input-field text-sm lg:text-base"
            />
          </div>

          <div>
            <label className="label text-xs lg:text-sm">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="input-field text-sm lg:text-base"
              rows={3}
              placeholder="Add any notes about this lead..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary text-sm lg:text-base"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary text-sm lg:text-base disabled:opacity-50"
            >
              {loading ? 'Saving...' : lead ? 'Update Lead' : 'Add Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
